import type { ReadableStream } from 'node:stream/web';

export type Extensible = Record<string, unknown>;

export type ThreadBlockType =
  | 'progress'
  | 'user'
  | 'assistant'
  | 'queue-operation'
  | 'file-history-snapshot'
  | 'summary'
  | 'pr-link'
  | 'last-prompt'
  | 'system'
  | 'custom-title'
  | 'plan';

export const threadBlockTypeValues = [
  'progress',
  'user',
  'assistant',
  'queue-operation',
  'file-history-snapshot',
  'summary',
  'pr-link',
  'last-prompt',
  'system',
  'custom-title',
  'plan',
] as const satisfies readonly ThreadBlockType[];

export const threadBlockTypes = new Set<ThreadBlockType>(threadBlockTypeValues);

export type ThreadLineEnvelopeBase = {
  cwd: string;
  gitBranch: string;
  isSidechain: boolean;
  parentUuid: string | null;
  sessionId: string;
  timestamp: string;
  userType: string;
  uuid: string;
  version: string;
};

export type UserMessage = {
  role: 'user';
  content: unknown;
} & Extensible;

export type AssistantMessage = {
  role: 'assistant';
  content: Record<string, unknown>[];
} & Extensible;

export type ProgressBlock = ({
  type: 'progress';
  index_in_thread: number;
  parentToolUseID?: string;
  toolUseID?: string;
  data: Record<string, unknown>;
} & Partial<ThreadLineEnvelopeBase> & Extensible);

export type UserBlock = ({
  type: 'user';
  index_in_thread: number;
  message: UserMessage;
} & Partial<ThreadLineEnvelopeBase> & Extensible);

export type AssistantBlock = ({
  type: 'assistant';
  index_in_thread: number;
  message: AssistantMessage;
} & Partial<ThreadLineEnvelopeBase> & Extensible);

export type QueueOperationBlock = {
  type: 'queue-operation';
  index_in_thread: number;
} & Extensible;

export type FileHistorySnapshotBlock = {
  type: 'file-history-snapshot';
  index_in_thread: number;
} & Extensible;

export type SummaryBlock = {
  type: 'summary';
  index_in_thread: number;
  summary?: string;
} & Extensible;

export type PrLinkBlock = {
  type: 'pr-link';
  index_in_thread: number;
} & Extensible;

export type LastPromptBlock = {
  type: 'last-prompt';
  index_in_thread: number;
} & Extensible;

export type CustomTitleBlock = {
  type: 'custom-title';
  index_in_thread: number;
  customTitle?: string;
} & Extensible;

export type PlanBlock = {
  id: string;
  type: 'plan';
  index_in_thread: number;
  prosemirror_json: Record<string, unknown>;
  thread_file_id: string;
} & Extensible;

export type SystemBlock = ({
  type: 'system';
  index_in_thread: number;
  subtype:
    | 'stop_hook_summary'
    | 'turn_duration'
    | 'local_command'
    | 'session_meta'
    | 'cowork_artifacts';
} & Partial<ThreadLineEnvelopeBase> & Extensible);

export type StopHookSummarySystemBlock = SystemBlock & { subtype: 'stop_hook_summary' };
export type TurnDurationSystemBlock = SystemBlock & { subtype: 'turn_duration' };
export type LocalCommandSystemBlock = SystemBlock & { subtype: 'local_command' };
export type SessionMetaSystemBlock = SystemBlock & { subtype: 'session_meta' };
export type CoworkArtifactsSystemBlock = SystemBlock & {
  subtype: 'cowork_artifacts';
  data: { uploads: string[]; outputs: string[] };
};

export type ThreadBlock =
  | ProgressBlock
  | UserBlock
  | AssistantBlock
  | QueueOperationBlock
  | FileHistorySnapshotBlock
  | SummaryBlock
  | PrLinkBlock
  | LastPromptBlock
  | CustomTitleBlock
  | PlanBlock
  | SystemBlock;

export type ThreadBlocks = ThreadBlock[];

export type TranscriptStream = ReadableStream<string | Uint8Array>;

export type TranscriptParserLogger = {
  warn?: (...args: unknown[]) => void;
  error?: (...args: unknown[]) => void;
};

export type TranscriptHarness = 'claudeCode' | 'codex' | 'amp' | 'cursor' | 'cowork' | 'unspecified';

/**
 * Cowork only — filenames discovered alongside the session transcript
 * (under `<session>/uploads/` and `<session>/outputs/`). The cowork
 * parser surfaces these as a `system / cowork_artifacts` block prepended
 * to the transcript so the renderer can show users what files the
 * session referenced. The actual file *contents* are NOT uploaded in v1.
 */
export type CoworkArtifacts = {
  uploads: string[];
  outputs: string[];
};

export type LoadPlanFile = (args: {
  harness: TranscriptHarness;
  planFilePath: string;
}) => Promise<{ markdown: string; threadFileId: string } | null>;

export type TranscriptParserOptions = {
  logger?: TranscriptParserLogger;
  /** Claude Code only. Omit to keep ExitPlanMode as its original assistant block. */
  harness?: TranscriptHarness;
  /** Claude Code only. Omit to keep ExitPlanMode as its original assistant block. */
  loadPlanFile?: LoadPlanFile;
  /** Cowork only. When provided, the parser prepends a `cowork_artifacts` block. */
  coworkArtifacts?: CoworkArtifacts;
};

export type TranscriptParser = (
  transcript: TranscriptStream,
  options?: TranscriptParserOptions,
) => Promise<ThreadBlocks>;
